import React from "react";
import { Paper, Typography, Skeleton } from "@mui/material";
import { makeStyles } from "tss-react/mui";

const useStyles = makeStyles()((theme) => ({
	tileClickable: {
		cursor: "pointer",
		transition: "border-color .15s ease, transform .15s ease",
		"&:hover": {
			borderColor: theme.palette.text.secondary,
			transform: "translateY(-1px)",
		},
	},

	tile: {
		padding: theme.spacing(1.25, 1.75),
		display: "flex",
		flexDirection: "column",
		gap: 2,
		height: "100%",
		borderTop: "3px solid transparent",
	},
	labelRow: {
		display: "flex",
		alignItems: "center",
		gap: 6,
	},
	icon: {
		fontSize: 17,
		color: theme.palette.text.secondary,
		flexShrink: 0,
	},
	label: {
		fontSize: 11.5,
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: "0.04em",
		color: theme.palette.text.secondary,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	value: {
		fontSize: 23,
		fontWeight: 600,
		lineHeight: 1.15,
		color: theme.palette.text.primary,
		whiteSpace: "nowrap",
	},
	tileHero: {
		padding: theme.spacing(2.5, 3),
	},
	valueHero: {
		fontSize: 46,
		fontWeight: 600,
		lineHeight: 1.05,
		letterSpacing: "-0.02em",
	},
	valueEmpty: {
		color: theme.palette.text.disabled,
	},
	sub: {
		fontSize: 11.5,
		fontWeight: 500,
	},
	deltaRow: {
		display: "flex",
		alignItems: "baseline",
		flexWrap: "wrap",
		gap: 8,
		rowGap: 4,
	},
	delta: {
		fontSize: 11.5,
		fontWeight: 700,
		fontVariantNumeric: "tabular-nums",
		whiteSpace: "nowrap",
	},
	deltaGood: { color: "#0a7a33" },
	deltaBad: { color: "#c62828" },
	deltaFlat: { color: "#8a8a83" },
	target: {
		fontSize: 10.5,
		fontWeight: 600,
		padding: "1px 6px",
		borderRadius: 4,
		whiteSpace: "nowrap",
		alignSelf: "center",
	},
	targetOk: {
		color: "#0a7a33",
		backgroundColor: theme.palette.mode === "light" ? "#e8f5ec" : "#16301f",
	},
	targetBad: {
		color: "#c62828",
		backgroundColor: theme.palette.mode === "light" ? "#fdeaea" : "#33191a",
	},
	targetWarn: {
		color: "#b45309",
		backgroundColor: theme.palette.mode === "light" ? "#fdf3e3" : "#332a16",
	},
	targetNeutral: {
		color: theme.palette.mode === "light" ? "#5f6368" : "#a8adb3",
		backgroundColor: theme.palette.mode === "light" ? "#f1f3f4" : "#2a2d31",
	},
	hint: {
		fontSize: 10.5,
		lineHeight: 1.3,
		color: theme.palette.text.disabled,
		marginTop: 3,
		display: "-webkit-box",
		WebkitLineClamp: 2,
		WebkitBoxOrient: "vertical",
		overflow: "hidden",
	},
}));

const StatTile = ({ label, value, sub, subColor, accent, icon, loading, hero, hint,
	delta, deltaGoodWhenUp = true, deltaTitle,
	target, targetMet, targetLabel, targetTone,
	onClick }) => {
	const { classes, cx } = useStyles();

	if (loading) {
		return <Skeleton variant="rounded" height={hero ? 132 : 88} />;
	}

	const isZero = value === 0 || value === "0";

	return (
		<Paper
			elevation={0}
			variant="outlined"
			className={cx(classes.tile, hero && classes.tileHero, onClick && classes.tileClickable)}
			style={accent ? { borderTopColor: accent } : undefined}
			onClick={onClick}
			role={onClick ? "button" : undefined}
			tabIndex={onClick ? 0 : undefined}
			onKeyDown={onClick ? (e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					onClick(e);
				}
			} : undefined}
			title={onClick ? "Ver el detalle" : undefined}
		>
			<div className={classes.labelRow}>
				{icon && React.cloneElement(icon, { className: classes.icon })}
				<Typography className={classes.label}>{label}</Typography>
			</div>
			<div className={classes.deltaRow}>
				<Typography className={cx(classes.value, hero && classes.valueHero, isZero && classes.valueEmpty)}>
					{value}
				</Typography>
				{target != null && (
					<span
					className={cx(
						classes.target,
						targetTone === "neutral"
							? classes.targetNeutral
							: targetTone === "warn"
								? classes.targetWarn
								: targetTone === "bad" || (!targetTone && !targetMet)
									? classes.targetBad
									: classes.targetOk
					)}
					title={targetLabel}
				>
					{targetTone === "neutral"
						? target
						: `${targetTone === "warn" ? "!" : (targetTone ? targetTone === "ok" : targetMet) ? "✓" : "✕"} ${target}`}
					</span>
				)}
				{delta != null && Number.isFinite(delta) && (
					<span
						className={cx(
							classes.delta,
							delta === 0
								? classes.deltaFlat
								: (delta > 0) === deltaGoodWhenUp
									? classes.deltaGood
									: classes.deltaBad
						)}
						title={deltaTitle}
					>
						{delta === 0 ? "=" : (delta > 0 ? "▲" : "▼") + " " + Math.abs(delta) + "%"}
					</span>
				)}
			</div>
			{sub && (
				<Typography className={classes.sub} style={{ color: subColor }}>
					{sub}
				</Typography>
			)}
			{hint && <Typography className={classes.hint}>{hint}</Typography>}
		</Paper>
	);
};

export default StatTile;
